import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { API } from "@/lib/brand";
import BackToAdminLink from "@/components/BackToAdminLink";
import { RocketLaunch, ArrowClockwise, Trash } from "@phosphor-icons/react";

const STATUS_OPTIONS = ["pending", "planned", "in_progress", "shipped", "declined"];

export default function AdminRoadmapPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/roadmap`);
      setItems(res.data.items || []);
    } catch (e) {
      toast.error("Could not load roadmap");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user?.is_admin) load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.is_admin]);

  const setStatus = async (id, status) => {
    setItems((prev) => prev.map((i) => (i.id === id ? { ...i, status } : i)));
    try {
      await axios.patch(`${API}/admin/roadmap/${id}`, { status });
      toast.success(`Moved to ${status.replace("_", " ")}`);
    } catch {
      toast.error("Failed to update");
      load();
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this roadmap item?")) return;
    try {
      await axios.delete(`${API}/admin/roadmap/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const chip = (status) => {
    const s = {
      pending: "bg-black/10 border-black/30",
      planned: "bg-[#FFBD4A] border-black",
      in_progress: "bg-blue-100 border-blue-700 text-blue-900",
      shipped: "bg-emerald-100 border-emerald-700 text-emerald-900",
      declined: "bg-red-100 border-red-700 text-red-900",
    }[status];
    return (
      <span className={`text-[10px] uppercase tracking-widest font-heading px-2 py-1 rounded border ${s}`}>
        {status.replace("_", " ")}
      </span>
    );
  };

  if (user && !user.is_admin) return <Navigate to="/dashboard" replace />;

  return (
    <div className="space-y-6" data-testid="admin-roadmap-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <BackToAdminLink />
          <div className="section-eyebrow flex items-center gap-2">
            <RocketLaunch size={16} weight="bold" /> Admin · Roadmap
          </div>
          <h1 className="font-heading uppercase text-4xl mt-2">Roadmap curation</h1>
          <p className="text-black/75 mt-3 leading-relaxed">
            Every suggestion your users submit shows up here first as <strong>Pending</strong> —
            hidden from the public roadmap until you decide what to do with it. Move an item to
            Planned, In progress or Shipped to make it visible to everyone on the public
            /roadmap page and start collecting votes. Declined items also stay hidden, so you can
            triage without hurting feelings. Deleting is permanent (only use it for spam or
            duplicates). Votes are one-per-user and idempotent, so users can't game the ranking.
          </p>
        </div>
        <button
          type="button"
          className="btn-secondary"
          onClick={load}
          disabled={loading}
          data-testid="admin-roadmap-reload"
        >
          <ArrowClockwise size={14} weight="bold" /> {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      <div className="card p-0 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-roadmap-table">
            <thead className="bg-black/5">
              <tr className="text-left">
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Suggestion</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Status</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Votes</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Submitted</th>
                <th className="px-4 py-3 text-xs uppercase tracking-widest font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-black/10">
              {items.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-black/55">
                    {loading ? "Loading…" : "No suggestions yet."}
                  </td>
                </tr>
              ) : (
                items.map((item) => (
                  <tr key={item.id} data-testid={`admin-roadmap-row-${item.id}`}>
                    <td className="px-4 py-3 align-top">
                      <div className="font-semibold">{item.title}</div>
                      <p className="text-xs text-black/60 mt-1 max-w-xl line-clamp-2">
                        {item.description}
                      </p>
                      <p className="text-[10px] text-black/45 mt-1">
                        by {item.user_name || item.user_email}
                      </p>
                    </td>
                    <td className="px-4 py-3 align-top">{chip(item.status)}</td>
                    <td className="px-4 py-3 align-top">{item.votes}</td>
                    <td className="px-4 py-3 align-top text-xs whitespace-nowrap">
                      {new Date(item.created_at).toLocaleDateString("en-GB", {
                        day: "2-digit", month: "short", year: "numeric",
                      })}
                    </td>
                    <td className="px-4 py-3 align-top">
                      <div className="flex flex-wrap gap-1">
                        <select
                          className="input !text-xs !py-1 !px-2"
                          value={item.status}
                          onChange={(e) => setStatus(item.id, e.target.value)}
                          data-testid={`admin-roadmap-status-${item.id}`}
                        >
                          {STATUS_OPTIONS.map((s) => (
                            <option key={s} value={s}>{s.replace("_", " ")}</option>
                          ))}
                        </select>
                        <button
                          type="button"
                          onClick={() => remove(item.id)}
                          className="!px-2 !py-1 !text-xs rounded-lg border-[1.5px] border-red-600 text-red-700 hover:bg-red-50 font-semibold"
                          title="Delete"
                          data-testid={`admin-roadmap-delete-${item.id}`}
                        >
                          <Trash size={12} weight="bold" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
